/**
 * Utilidades para filtrar datos de boards
 */

/**
 * Filtra items por status básico (Open/Done)
 * @param {Array} items - Array de items
 * @param {string} status - Status a filtrar
 * @param {boolean} condition - Si true, incluye items con el status; si false, los excluye
 * @returns {Array} Items filtrados
 */
export const filterByStatus = (items, status, condition = true) => {
  if (!Array.isArray(items)) return [];

  if (condition) {
    return items.filter((item) => item.status === status);
  }
  return items.filter((item) => item.status !== status);
};

/**
 * Filtra items por offerStatus (para boards de offers)
 * @param {Array} items - Array de items
 * @param {string} status - Status del grupo
 * @returns {Array} Items filtrados
 */
export const filterByOfferStatus = (items, status) => {
  if (!Array.isArray(items)) return [];

  const filters = {
    "Offer ID": (item) =>
      !["COMPLETED", "INACTIVE", "NOT PROFITABLE"].includes(item.offerStatus),
    Completed: (item) => item.offerStatus === "COMPLETED",
    Inactive: (item) => item.offerStatus === "INACTIVE",
    "Not Profitable": (item) =>
      item.offerStatus === "NOT PROFITABLE" || item.status === "Not Profitable",
  };

  const filterFn = filters[status];
  return filterFn ? items.filter(filterFn) : items;
};

/**
 * Filtra items por multiple status
 * @param {Array} items - Array de items
 * @param {Array} statusList - Lista de status a incluir
 * @param {string} statusField - Campo del status (default: 'status')
 * @returns {Array} Items filtrados
 */
export const filterByMultipleStatus = (
  items,
  statusList,
  statusField = "status"
) => {
  if (!Array.isArray(items) || !Array.isArray(statusList)) return [];

  return items.filter((item) => statusList.includes(item[statusField]));
};

/**
 * Filtra items por propietario/owner
 * @param {Array} items - Array de items
 * @param {string} ownerField - Campo del propietario (ej: 'owner.username', 'owner.email')
 * @param {string} ownerValue - Valor del propietario
 * @returns {Array} Items filtrados
 */
export const filterByOwner = (items, ownerField, ownerValue) => {
  if (!Array.isArray(items)) return [];

  return items.filter((item) => {
    const fieldParts = ownerField.split(".");
    let value = item;

    for (const part of fieldParts) {
      value = value?.[part];
    }

    return value === ownerValue;
  });
};

/**
 * Filtra items por rango de fechas
 * @param {Array} items - Array de items
 * @param {string} dateField - Campo de fecha
 * @param {Date} startDate - Fecha inicio
 * @param {Date} endDate - Fecha fin
 * @returns {Array} Items filtrados
 */
export const filterByDateRange = (items, dateField, startDate, endDate) => {
  if (!Array.isArray(items)) return [];

  return items.filter((item) => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= startDate && itemDate <= endDate;
  });
};

/**
 * Función genérica para separar items en grupos
 * @param {Array} items - Array de items
 * @param {Array} groups - Array de nombres de grupos
 * @param {function} filterFunction - Función de filtrado
 * @returns {Object} Objeto con items separados por grupo
 */
export const separateItemsByGroups = (items, groups, filterFunction) => {
  if (!Array.isArray(items) || !Array.isArray(groups)) {
    return {};
  }

  const result = {};

  groups.forEach((group) => {
    result[group] = filterFunction(items, group);
  });

  return result;
};

/**
 * Función específica para TasksAluna - separar por status
 * @param {Array} items - Array de items
 * @returns {Object} { open: Array, done: Array }
 */
export const separateTasksAlunaItems = (items) => {
  return {
    open: filterByStatus(items, "Done", false),
    done: filterByStatus(items, "Done", true),
  };
};

/**
 * Función específica para MasterBranding - separar por offerStatus
 * @param {Array} items - Array de items
 * @returns {Object} { offerID: Array, completed: Array }
 */
export const separateMasterBrandingItems = (items) => {
  return {
    offerID: filterByOfferStatus(items, "Offer ID"),
    completed: filterByOfferStatus(items, "Completed"),
  };
};

/**
 * Función específica para MasterPerformance - separar por offerStatus
 * @param {Array} items - Array de items
 * @returns {Object} { offerID: Array, inactive: Array, notProfitable: Array }
 */
export const separateMasterPerformanceItems = (items) => {
  return {
    offerID: filterByOfferStatus(items, "Offer ID"),
    inactive: filterByOfferStatus(items, "Inactive"),
    notProfitable: filterByOfferStatus(items, "Not Profitable"),
  };
};

/**
 * Función específica para MasterCPS - separar por offerStatus
 * @param {Array} items - Array de items
 * @returns {Object} { offerID: Array, inactive: Array, pixelIntegration: Array }
 */
export const separateMasterCpsItems = (items) => {
  const filterByOfferStatusCps = (items, status) => {
    if (!Array.isArray(items)) return [];

    if (status === "Offer ID") {
      return items.filter(
        (item) =>
          item.offerStatus !== "INACTIVE" &&
          item.offerStatus !== "PIXEL INTEGRATION"
      );
    } else if (status === "Inactive") {
      return items.filter((item) => item.offerStatus === "INACTIVE");
    } else if (status === "Pixel Integration") {
      return items.filter((item) => item.offerStatus === "PIXEL INTEGRATION");
    }
    return items;
  };

  return {
    offerID: filterByOfferStatusCps(items, "Offer ID"),
    inactive: filterByOfferStatusCps(items, "Inactive"),
    pixelIntegration: filterByOfferStatusCps(items, "Pixel Integration"),
  };
};

/**
 * Filtra items por año en el campo table
 * @param {Array} items - Array de items
 * @param {number} year - Año a filtrar
 * @param {boolean} condition - Si true, incluye items con el año; si false, los excluye
 * @returns {Array} Items filtrados
 */
export const filterByYear = (items, year, condition = true) => {
  if (!Array.isArray(items)) return [];

  if (condition) {
    return items.filter(
      (item) => item.table && item.table.includes(year.toString())
    );
  }
  return items.filter(
    (item) => !item.table || !item.table.includes(year.toString())
  );
};

/**
 * Función específica para Validaciones - separar por año
 * @param {Array} items - Array de items
 * @param {number} currentYear - Año actual
 * @returns {Object} { items: Array, itemsDone: Array }
 */
export const separateValidacionesItems = (
  items,
  currentYear = new Date().getFullYear()
) => {
  return {
    items: filterByYear(items, currentYear, true),
    itemsDone: filterByYear(items, currentYear, false),
  };
};

/**
 * Función específica para ProspectingAfiliados - separar por owner
 * @param {Array} items - Array de items
 * @returns {Object} Objeto con items separados por owner
 */
export const separateProspectingAfiliadosItems = (items) => {
  const filterByOwner = (rows, status) => {
    return rows.filter((item) => {
      if (item.owner?.username === status) {
        return true;
      } else if (status === "Camila Velloso" && item.owner == null) {
        return true;
      }
      return false;
    });
  };

  return {
    belenSuero: filterByOwner(items, "Belén Suero"),
    mariaCamila: filterByOwner(items, "Camila Velloso"),
    carolinaRacana: filterByOwner(items, "Carolina Racana"),
    katalinaPalomino: filterByOwner(items, "Katalina Palomino"),
    saraLopez: filterByOwner(items, "Sara López Graciano"),
  };
};

/**
 * Ordena items por fecha (más recientes primero)
 * @param {Array} items - Array de items
 * @returns {Array} Items ordenados por fecha
 */
export const sortItemsByDate = (items) => {
  if (!Array.isArray(items)) return [];

  return items.sort((a, b) => {
    const dateA = new Date(a.publishedAt || a.createdAt || 0);
    const dateB = new Date(b.publishedAt || b.createdAt || 0);
    return dateB - dateA; // Orden descendente (más recientes primero)
  });
};

/**
 * Función específica para AlunaSocialMedia - separar por status con ordenación por fecha
 * @param {Array} items - Array de items
 * @returns {Object} { items: Array, itemsDone: Array }
 */
export const separateAlunaSocialMediaItems = (items) => {
  const filterItemsByStatus = (items, status, condition) => {
    let filteredItems;
    if (condition) {
      filteredItems = items?.filter((item) => item.status === status);
    } else {
      filteredItems = items?.filter((item) => item.status !== status);
    }

    // Ordenar los items filtrados por fecha
    return sortItemsByDate(filteredItems);
  };

  return {
    items: filterItemsByStatus(items, "Done", false),
    itemsDone: filterItemsByStatus(items, "Done", true),
  };
};
